import type { MouseEventHandler, Ref } from 'react'
import cx from 'classnames'
import styles from './DocumentPicker.module.css'
import { type Observable } from 'rxjs'
import { ModalSheet, ModalSheetContent } from '@/components/ModalSheet'
import { ModalSheetSearchHeader } from '@/components/ModalSheet/ModalSheetSearchHeader'
import { type Id } from '@/infra/id'
import { useCombobox } from 'downshift'
import { getFileIconType } from '@/utils/icon-util'
import { FileTypeIcon } from '@/components/FileTypeIcon'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { ContentError } from '@/components/ContentError'

export interface DocumentPickerItem {
  id: Id
  title: string
  fileName: string
  isSelected: boolean
  isSelecting: boolean
  onSelect: () => unknown
}

export interface DocumentPickerProps {
  showing: boolean
  onClose: () => unknown
  focus$?: Observable<unknown>
  searchText: string
  onSearchTextChange: (text: string) => unknown
  items: DocumentPickerItem[]
  isDone: boolean
  /**
   * Why the documents failed to load, shown in place of the list's end.
   */
  error?: unknown
  scrollMonitorRef: Ref<HTMLDivElement>
  searchPlaceholder?: string
  testId?: string
}

// Suppress ref errors in Downshift.
// We don't always render the underlying component,
// so Downshift complains. But we got this.
const SUPPRESS = { suppressRefError: true }

export function DocumentPicker({
  showing,
  onClose,
  focus$,
  searchText,
  onSearchTextChange,
  items,
  isDone,
  error,
  scrollMonitorRef,
  testId,
  searchPlaceholder = 'Type to search. You can use arrow keys and Enter here.',
}: DocumentPickerProps) {
  const { getItemProps, getMenuProps, getInputProps, highlightedIndex } =
    useCombobox({
      isOpen: showing,
      items,
      selectedItem: null,
      onSelectedItemChange: (c) => c.selectedItem?.onSelect(),
      isItemDisabled: (item) => item.isSelecting,
    })

  // Downshift adds the ref under the default `refKey` at runtime, but its typings omit it.
  const { ref: inputRef, onKeyDown: onInputKeyDown } = getInputProps(
    {},
    SUPPRESS,
  ) as ReturnType<typeof getInputProps> & { ref: Ref<HTMLInputElement> }

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onClose}
      {...getInputProps({}, SUPPRESS)}
    >
      <ModalSheetSearchHeader
        bordered
        withCloseButton
        focus$={focus$}
        placeholder={searchPlaceholder}
        testId={`${testId}-search-input`}
        onChange={onSearchTextChange}
        value={searchText}
        onKeyDown={onInputKeyDown}
        lonelyChild={
          // These conditions will be true when we don't render anything else.
          items.length === 0 && isDone && Boolean(searchText)
        }
        ref={inputRef}
      />
      <ModalSheetContent {...getMenuProps({}, SUPPRESS)}>
        {items.map((item, i) => {
          const itemProps = getItemProps({
            index: i,
            key: item.id,
            item,
          })
          return (
            <DocumentItem
              key={item.id}
              item={item}
              ref={itemProps.ref}
              testId={`${testId}-document-${item.id}`}
              onMouseMove={itemProps.onMouseMove}
              onClick={itemProps.onClick}
              highlighted={i === highlightedIndex}
            />
          )
        })}
        {showing && Boolean(error) && (
          <ContentError error={error} size="section" />
        )}
        {showing && !error && !isDone && (
          <div
            className={cx(styles.loading, items.length === 0 && styles.empty)}
            ref={scrollMonitorRef}
          >
            <Spinner />
          </div>
        )}
      </ModalSheetContent>
    </ModalSheet>
  )
}

interface DocumentItemProps {
  item: DocumentPickerItem
  highlighted: boolean
  onClick?: MouseEventHandler
  onMouseMove?: MouseEventHandler
  ref?: Ref<HTMLDivElement>
  testId?: string
}

function DocumentItem({
  item,
  highlighted,
  onClick,
  onMouseMove,
  ref,
  testId,
}: DocumentItemProps) {
  const isSelected = item.isSelected
  const icon = getFileIconType(item.fileName)
  const fileName = item.fileName
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- keyboard selection and focus stay on the combobox input (aria-activedescendant); focusing options would steal focus from it
    <div
      role="option"
      aria-selected={isSelected}
      className={cx(styles.item, highlighted && styles.highlighted)}
      data-test={testId}
      ref={ref}
      onClick={onClick}
      onMouseMove={onMouseMove}
    >
      <div className={styles.icon}>
        <FileTypeIcon
          height={36}
          primaryColor={icon.color}
          strokeColor="#bcbcbe"
          type={icon.type}
        />
      </div>
      <div className={styles.info}>
        <div className={styles.title} data-test={`${testId}-title`}>
          {item.title}
        </div>
        <div className={styles.subtitle}>{fileName}</div>
      </div>
      <div className={styles.extras}>
        {item.isSelecting ? (
          <Spinner />
        ) : isSelected ? (
          <div className={styles.icon}>
            <Icon name="radio-checked" />
          </div>
        ) : null}
      </div>
    </div>
  )
}
